import SlotPageWrapper from '@/components/SlotPageWrapper'
import { WorkOSUser } from '@/components/UserProfileSettingDetail'
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/shadcn/form'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/shadcn/input-otp'
import { Label } from '@/components/shadcn/label.tsx'
import showToastNotification from '@/hooks/useShowToast'
import { trpc } from '@/utils/trpc.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import { ArrowLeftIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocalStorage } from 'usehooks-ts'
import { z } from 'zod'

type SearchParams = {
  redirect?: string
}

export const Route = createFileRoute('/verify-email')({
  component: VerifyEmail,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    return search
  },
})

function VerifyEmail() {
  const { redirect } = Route.useSearch()

  const [timer, setTimer] = useState<number>(0) // Timer state (in seconds)

  const [resend_timer, set_resend_timer] = useLocalStorage<string | undefined>('resend_timer', undefined)

  // Function to start/resume the timer
  const startTimer = () => {
    const expirationTime = Date.now() + 180 * 1000 // 3 minutes from now
    set_resend_timer(expirationTime.toString()) // Store timestamp
    setTimer(180)
  }

  // Retrieve the timer from localStorage on mount
  useEffect(() => {
    const storedExpiration = resend_timer
    if (storedExpiration) {
      const remainingTime = Math.floor((parseInt(storedExpiration) - Date.now()) / 1000)
      if (remainingTime > 0) {
        setTimer(remainingTime) // Resume timer if still active
      } else {
        set_resend_timer(undefined) // Cleanup if expired
      }
    }
  }, [])

  // Countdown effect
  useEffect(() => {
    if (timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            set_resend_timer(undefined) // Remove timer when it reaches zero
            clearInterval(countdown)
            return 0
          }
          return prev - 1
        })
      }, 1000)
      return () => clearInterval(countdown)
    }
  }, [timer])

  const navigate = useNavigate()

  const { mutate, isPending } = trpc.verifyUserCode.useMutation()
  const cosPlan = localStorage.getItem('cos_plan')

  const { mutate: resendCode, isPending: isResendCodePending } = trpc.sendUserVerificationCode.useMutation({
    onError: (error) => {
      showToastNotification('error', {
        message: error.message,
      })
    },
  })

  const {
    isLoading: authIsLoading,
    isRefetching,
    refetch: refetchUserDetails,
  } = trpc.me.useQuery(null, { enabled: false })

  const [storedUser, setStoredUser] = useLocalStorage<WorkOSUser | undefined>('_wos_user', undefined)

  const handleResendCode = () => {
    if (storedUser) {
      resendCode({ userId: storedUser.id })
      startTimer()
    } else {
      console.error('No user ID found')
      navigate({ to: '/login', search: { redirect } })
    }
  }

  const verifyEmailFormSchema = z.object({
    otp: z.string({ required_error: 'OTP is required' }),
  })

  type VerifyEmailFormValues = z.infer<typeof verifyEmailFormSchema>

  const defaultValues: Partial<VerifyEmailFormValues> = {
    otp: '',
  }

  const form = useForm<VerifyEmailFormValues>({
    resolver: zodResolver(verifyEmailFormSchema),
    defaultValues,
  })

  async function onSubmit({ otp }: VerifyEmailFormValues) {
    mutate(
      {
        code: otp,
        subscribed: storedUser?.newsletter ?? true,
      },
      {
        onSuccess: (data) => {
          if (data && data.emailVerified) {
            refetchUserDetails().then((authUser) => {
              if (typeof localStorage !== 'undefined' && !authIsLoading && !isRefetching) {
                setStoredUser(authUser.data)
              }

              showToastNotification('success', {
                message: 'Verification successful',
              })
              if (redirect) {
                if (redirect?.toString() === 'true') {
                  const lastPath = sessionStorage.getItem('lastPath')
                  if (lastPath) {
                    window.location.href = lastPath
                  } else {
                    if (cosPlan) {
                      navigate({ to: '/checkout', search: { redirect } })
                    } else navigate({ to: '/plans', search: { redirect } })
                  }
                } else if (redirect?.toString().includes('/')) {
                  window.location.href = redirect
                }
              } else if (cosPlan) {
                navigate({ to: '/checkout', search: { redirect } })
              } else navigate({ to: '/plans', search: { redirect } })
            })
          }
        },
        onError: (error) => {
          showToastNotification('error', {
            message: error.message,
            description:
              'Please check the 6-digit code and try again. If you didn’t receive the code, request a new one.',
          })
        },
      },
    )
  }

  return (
    <SlotPageWrapper>
      <div className={'flex flex-col items-start gap-2 w-full'}>
        <Button
          onClick={() => navigate({ to: '/login', search: { redirect } })}
          variant={'ghost'}
          className={'flex gap-1.5 items-center'}
        >
          <ArrowLeftIcon size={'1rem'} />
          <span>Back</span>
        </Button>
        <h4 className={'text-2xl text-center text-themeforeground font-semibold'}>Verify your email address</h4>
        <p>
          We sent a verification code to <span className={'font-medium'}>{storedUser && storedUser.email}</span>. This
          code is valid for 10 minutes.
        </p>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 w-full">
          <div className={'flex flex-col gap-6'}>
            <FormField
              control={form.control}
              name="otp"
              render={({ field }) => (
                <FormItem>
                  <Label>6-Digit Code</Label>
                  <FormControl>
                    <InputOTP {...field} maxLength={6} pattern={REGEXP_ONLY_DIGITS}>
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div>
            <Button
              disabled={isPending || form.watch('otp')?.length < 6 || form.getValues('otp') === ''}
              type={'submit'}
              loading={isPending}
              className="w-full"
            >
              {isPending ? 'Submitting...' : 'Verify Email'}
            </Button>
            <div>
              <div className={'block text-center mt-1.5 text-sm'}>
                <span>Didn’t receive an email?</span>{' '}
                <Button
                  variant={'link'}
                  disabled={isResendCodePending || timer > 0}
                  onClick={timer === 0 ? handleResendCode : undefined}
                  className={'underline font-medium p-0'}
                >
                  {timer > 0 ? `Resend in ${Math.floor(timer / 60)}:${('0' + (timer % 60)).slice(-2)}` : 'Resend Code'}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </SlotPageWrapper>
  )
}
